import HandleClient, { KoraLabsProvider, } from "@koralabs/adahandle-sdk";
import { AssetAmount } from "@sundaeswap/asset";
import { ADA_METADATA } from "../../constants/cardano";
export const FALLBACK_DECIMAL = 6;
/**
 * Represents a collection of mapped assets and provides utility functions for interacting with them.
 */
export class MappedAssets {
    /**
     * Creates a new MappedAssets instance.
     *
     * @param assetIds - Array of asset IDs.
     * @param metadata - Array of metadata corresponding to the asset IDs.
     * @param utxos - Array of UTXO objects corresponding to the asset IDs.
     * @param walletAmount - Array of wallet amounts corresponding to the asset IDs.
     * @param handleClient - An instance of the HandleClient.
     * @param metadataResolver - (Optional) A resolver function for metadata.
     * @private
     */
    constructor(assetIds, metadata, utxos, walletAmount, handleClient, metadataResolver) {
        this.data = new Map();
        this.handleClient = handleClient;
        this.metadataResolver = metadataResolver;
        assetIds.forEach((assetId, index) => {
            this.data.set(assetId, {
                metadata: metadata[index],
                quantity: new AssetAmount(walletAmount[index], metadata[index]),
                utxos: utxos[index],
            });
        });
    }
    /**
     * Constructs a new instance of MappedAssets from the provided API balance data.
     *
     * @param args - An object containing parameters for constructing the MappedAssets.
     * @returns A new MappedAssets instance.
     * @static
     */
    static async getMapFromApiBalance({ apiBalance, apiUtxos, metadataResolver, ignoreNFTs = true, useHandles = false, network = 1, }) {
        const assetIds = [ADA_METADATA.assetId];
        const assetAmounts = [apiBalance?.coin];
        const assetUtxos = [
            apiUtxos.map(({ index, transaction_id }) => ({
                index: Number(index),
                hash: transaction_id,
            })),
        ];
        const adahandle = new HandleClient({
            context: network,
            provider: new KoraLabsProvider(network),
        });
        if (apiBalance?.multiasset) {
            for (const policyID in apiBalance.multiasset) {
                const policyAssets = apiBalance.multiasset[policyID];
                for (const assetName in policyAssets) {
                    const matchingUtxos = apiUtxos
                        .filter(({ amount }) => {
                        if (amount?.multiasset?.[policyID]?.[assetName]) {
                            return true;
                        }
                        return false;
                    })
                        .map(({ index, transaction_id }) => ({
                        index: Number(index),
                        hash: transaction_id,
                    }));
                    if (useHandles && adahandle.isADAHandle(policyID)) {
                        assetIds.push(`${policyID}.${assetName}`);
                        assetAmounts.push(policyAssets[assetName]);
                        assetUtxos.push(matchingUtxos);
                        continue;
                    }
                    if (policyAssets[assetName] === "1" && ignoreNFTs) {
                        continue;
                    }
                    assetIds.push(`${policyID}.${assetName}`);
                    assetAmounts.push(policyAssets[assetName]);
                    assetUtxos.push(matchingUtxos);
                }
            }
        }
        const assetMetadata = metadataResolver
            ? await metadataResolver(assetIds, [ADA_METADATA], adahandle)
            : assetIds.map((id, index) => index === 0
                ? ADA_METADATA
                : { assetId: id, decimals: FALLBACK_DECIMAL });
        let resolvedMetadataMap = {};
        for (let i = 0; i < assetMetadata.length; i++) {
            resolvedMetadataMap[assetMetadata[i].assetId] = assetMetadata[i];
        }
        const orderedMetadata = assetIds.map((id) => resolvedMetadataMap[id] || { assetId: id, decimals: FALLBACK_DECIMAL });
        return new MappedAssets(assetIds, orderedMetadata, assetUtxos, assetAmounts, adahandle, metadataResolver);
    }
    /**
     * Checks if an asset with the given ID exists within the data map.
     *
     * @param assetId - The ID of the asset to check.
     * @returns True if the asset exists, false otherwise.
     * @private
     */
    assetExists(assetId) {
        return this.data.has(assetId);
    }
    /**
     * Retrieves handle data for a given handle name.
     *
     * @template T - Extends IAssetAmountMetadata type.
     * @param {string} handle - The hex encoded Handle name.
     * @returns {Promise<IHandle | T>} - Returns either an IHandle or metadata of the type T.
     * @throws Will throw an error if the asset with the given ID does not exist.
     * @async
     */
    async getHandleData(handle) {
        const activePolicyIds = this.handleClient.getActivePolicyIds();
        const possibleAssetIds = activePolicyIds.map((policyId) => `${policyId}.${handle}`);
        let matchingHandle = "";
        for (let id of possibleAssetIds) {
            if (this.assetExists(id)) {
                matchingHandle = id;
                break;
            }
        }
        if (!matchingHandle) {
            throw new Error("Asset with that ID does not exist in map!");
        }
        let data;
        if (HandleClient.isCIP68({ value: handle })) {
            const handleData = await this.handleClient
                .provider()
                .getAllData({ value: handle });
            data = {
                ...handleData,
                cipType: "cip68",
            };
        }
        else {
            data = {
                ...this.data.get(matchingHandle).metadata,
                cipType: "cip25",
            };
        }
        return data;
    }
    /**
     * Retrieves all handles along with their associated data.
     *
     * @returns An array of handles with data.
     * @async
     */
    async getHandlesWithData() {
        const handlesInWallet = this.getHandles();
        return Promise.all(handlesInWallet.map(([, { metadata }]) => {
            const name = metadata.assetId.split(".")[1];
            return this.getHandleData(name);
        }));
    }
    /**
     * Checks if an asset with the given ID exists within the data map.
     *
     * @param assetId - The ID of the asset to check.
     * @returns True if the asset exists, false otherwise.
     * @private
     */
    async getAssetDataById(assetId) {
        const adaIds = ["", ".", "ada.lovelace"];
        if (!this.assetExists(assetId) && !adaIds.includes(assetId)) {
            let metadata = null;
            if (this.metadataResolver) {
                metadata = (await this.metadataResolver([assetId], [null], this.handleClient))[0];
            }
            return {
                metadata,
                quantity: new AssetAmount("0", metadata ?? 0),
                utxos: [],
            };
        }
        return this.data.get(adaIds.includes(assetId) ? "ada.lovelace" : assetId);
    }
    /**
     * Retrieves the amount for a given asset ID.
     *
     * @param assetId - The ID of the asset for which the amount is being retrieved.
     * @returns The asset amount.
     * @async
     */
    async getAmount(assetId) {
        const { quantity } = await this.getAssetDataById(assetId);
        return quantity;
    }
    /**
     * Retrieves all handles.
     *
     * @template T - Type of the asset amount metadata.
     * @returns An array of handles.
     */
    getHandles() {
        return this.getArray()?.filter(([key]) => this.handleClient.isADAHandle(key.split(".")?.[0]));
    }
    /**
     * Retrieves all fungible assets.
     *
     * @template T - Type of the asset amount metadata.
     * @returns An array of fungible assets.
     */
    getFungibleAssets() {
        return this.getArray()?.filter(([_key, { quantity }]) => quantity.amount !== 1n);
    }
    /**
     * Retrieves the data as an array.
     *
     * @template T - Type of the asset amount metadata.
     * @returns An array representation of the data.
     */
    getArray() {
        return Array.from(this.data);
    }
    /**
     * Retrieves the data map.
     *
     * @returns The data map.
     */
    getMap() {
        return this.data;
    }
}
//# sourceMappingURL=MappedAssets.js.map